/**
 * Dates « civiles » — un jour calendaire, sans heure ni fuseau.
 *
 * Le moteur de règles ne manipule jamais d'objet Date : toutes les comparaisons
 * se font sur des chaînes `YYYY-MM-DD` interprétées en UTC. C'est ce qui rend le
 * moteur pur et reproductible — un test qui passe à Paris passe aussi sur un
 * serveur en UTC, et l'heure d'été ne décale rien.
 */
const MS_PAR_JOUR = 86_400_000;
const FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/;
export function estJourISO(v) {
    if (typeof v !== 'string')
        return false;
    const m = FORMAT.exec(v);
    if (!m)
        return false;
    return versEpoch(v) !== null;
}
/** Nombre de millisecondes UTC du jour, ou `null` si la chaîne n'est pas un jour valide. */
function versEpoch(jour) {
    const m = FORMAT.exec(jour);
    if (!m)
        return null;
    const [, a, mo, j] = m;
    const annee = Number(a);
    const mois = Number(mo);
    const jourDuMois = Number(j);
    if (mois < 1 || mois > 12 || jourDuMois < 1 || jourDuMois > 31)
        return null;
    const t = Date.UTC(annee, mois - 1, jourDuMois);
    const d = new Date(t);
    // Rejette les dates qui « débordent » (31 février → 3 mars).
    if (d.getUTCFullYear() !== annee || d.getUTCMonth() !== mois - 1 || d.getUTCDate() !== jourDuMois) {
        return null;
    }
    return t;
}
function epochOuErreur(jour) {
    const t = versEpoch(jour);
    if (t === null)
        throw new RangeError(`Jour ISO invalide : ${JSON.stringify(jour)}`);
    return t;
}
/** Nombre de jours de `depuis` vers `jusqua`. Positif si `jusqua` est postérieur. */
export function joursEntre(depuis, jusqua) {
    return Math.round((epochOuErreur(jusqua) - epochOuErreur(depuis)) / MS_PAR_JOUR);
}
/** Décale un jour de `n` jours (n peut être négatif). */
export function ajouterJours(jour, n) {
    return depuisDateUTC(new Date(epochOuErreur(jour) + n * MS_PAR_JOUR));
}
export function comparerJours(a, b) {
    return epochOuErreur(a) - epochOuErreur(b);
}
export function estAvant(a, b) {
    return comparerJours(a, b) < 0;
}
export function estApres(a, b) {
    return comparerJours(a, b) > 0;
}
function depuisDateUTC(d) {
    const a = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const j = String(d.getUTCDate()).padStart(2, '0');
    return `${a}-${m}-${j}`;
}
/**
 * Jour calendaire d'un instant, lu dans un fuseau donné.
 *
 * L'API appelle ceci avec `Europe/Paris` pour savoir quel jour il est côté
 * métier, puis passe le résultat au moteur. Un serveur en UTC à 23 h 30 doit
 * voir le même « aujourd'hui » que l'utilisateur à 1 h 30 du matin.
 */
export function jourDansFuseau(instant, fuseau = 'Europe/Paris') {
    const parties = new Intl.DateTimeFormat('en-CA', {
        timeZone: fuseau,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
    return parties; // en-CA produit déjà YYYY-MM-DD
}
const MOIS_FR = [
    'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];
/** Format court à la française : « 16 sept. ». Repris du prototype. */
export function formatCourt(jour) {
    if (!jour)
        return '—';
    const t = versEpoch(jour);
    if (t === null)
        return '—';
    const d = new Date(t);
    return `${d.getUTCDate()} ${MOIS_FR[d.getUTCMonth()]}`;
}
/**
 * Jour civil d'une chaîne ISO complète, lu en UTC.
 *
 * La base stocke ses dates métier en chaînes ISO à minuit UTC
 * (`2026-08-29T00:00:00.000Z`) : le jour voulu est celui des 10 premiers
 * caractères. Le lire dans un fuseau local décalerait `2026-08-29T00:00:00Z`
 * au 28 août pour tout fuseau à l'ouest de Greenwich.
 *
 * Renvoie `null` sur une valeur vide ou illisible, jamais une date inventée.
 */
export function jourDeIso(iso) {
    if (!iso)
        return null;
    if (iso instanceof Date) {
        return Number.isNaN(iso.getTime()) ? null : depuisDateUTC(iso);
    }
    const brut = String(iso).trim();
    if (!brut)
        return null;
    const jour = brut.slice(0, 10);
    if (estJourISO(jour))
        return jour;
    /*
     * Repli : une date rendue dans le format de `Date.prototype.toString`.
     *
     * **Ce n'est pas une coquetterie, c'est un vrai chemin de lecture.** Treize
     * commandes sur 84 portent `dateCommande` en type Date alors que le schéma la
     * déclare en chaîne. Mongoose caste donc à la LECTURE, et rend
     * « Mon Sep 14 2026 10:49:07 GMT+0200 (…) ». Les dix premiers caractères
     * valent alors « Mon Sep 14 » : illisible, donc `null`, donc le repli à
     * `1970-01-01` du présentateur — et la fiche annonçait « reçue le 1 janv. »
     * pour une commande du 14 septembre.
     *
     * Le défaut ne se voyait QUE sur la fiche : la liste lit en `lean()`, qui ne
     * caste pas et rend la Date telle quelle. Deux chemins de lecture, deux
     * vérités — exactement le genre d'écart qu'on ne trouve pas en relisant.
     *
     * `new Date` relit ce format sans ambiguïté : c'est JavaScript qui l'a écrit.
     * Une chaîne qui n'est pas une date reste `null` — on ne devine toujours pas.
     */
    /*
     * Une forme ISO invalide reste invalide, et ne passe PAS par le repli.
     *
     * `2026-02-31` n'existe pas ; le relire avec `new Date` donnerait le 3 mars —
     * une date plausible et fausse, c'est-à-dire le pire résultat possible. Le
     * repli ne sert qu'aux chaînes qui ne prétendent pas être de l'ISO.
     */
    if (/^\d{4}-\d{2}-\d{2}/.test(brut))
        return null;
    const relu = new Date(brut);
    return Number.isNaN(relu.getTime()) ? null : depuisDateUTC(relu);
}
/** Inverse de `jourDeIso` : un jour civil vers la chaîne ISO stockée en base. */
export function isoDeJour(jour) {
    if (!jour || !estJourISO(jour))
        return null;
    return `${jour}T00:00:00.000Z`;
}
//# sourceMappingURL=dates.js.map